import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowRight, Lock, Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient, { ApiClientError } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { BusinessDay, CashDrawerMovement } from '@/types'
import { netCashAdjustments } from './dayCloseHelpers'
import { countNeedingItems } from '@/lib/inventoryFlow/marketLogic'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import { TenderCountStep } from './TenderCountStep'
import { StepIndicator } from './StepIndicator'
import { ReviewStatus } from './ReviewStatus'
import { ReviewTable, type ReviewRow } from './ReviewTable'
import {
  DEFAULT_VARIANCE_THRESHOLD,
  type ExpectedBreakdownSpec,
  type TenderPhrasing,
} from './tenderMath'

/** One payment type's short/over on the review step. */
interface TenderRow {
  label: string
  entered: boolean
  over: number
  short: number
  /** True once the short/over crosses the note threshold. */
  needsNote: boolean
  /** Words for the two directions. Drawers are "over"/"short"; the marketplace app is "more/less in app". */
  phrasing?: TenderPhrasing
}

interface CloseDayWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  day: BusinessDay
  cashSales: number
  cardSales: number
  /** Online sales EXCLUDING platform-settled receivables (F-MM-N23). */
  onlineSales: number
  /** F-MM-N23: online tender the marketplace pays out later — shown, never counted. */
  platformSettledOnline?: number
  /**
   * F-CTRL-N67: what the tenant calls its marketplace order type ("Foodpanda").
   * Names the dedicated count step and every line that mentions it. Defaults
   * to "Foodpanda" — the only marketplace any tenant runs today.
   */
  marketplaceLabel?: string
  /** Real open orders still holding tables/revenue (F-CTRL-N25). A non-zero
   *  count requires a closing note before the day can lock. */
  unresolvedOpenOrders: number
  movements: CashDrawerMovement[] | undefined
  /**
   * True while the parent's cash-sales / movements queries are loading for
   * the first time. Until they settle, `cashSales` and `movements` default
   * to 0 / [], which would make the wizard's variance computation lie:
   * a staged day with a real Rs 5,000 shortfall would briefly render as
   * "drawer matches!" because expected_cash collapses to opening_cash.
   * The review step suppresses its banner + locks Finish until this is false.
   */
  isLoadingSummary: boolean
  /**
   * F-MM-N30: tab payments received today, per tender. Already INCLUDED in
   * cashSales / cardSales / onlineSales — only used to show them as their own
   * line in each "where does this come from" breakdown.
   */
  tabSettlements?: { cash: number; card: number; online: number }
}

type Step = 'count-cash' | 'count-card' | 'count-online' | 'count-marketplace' | 'review'

const BASE_STEP_ORDER: Step[] = ['count-cash', 'count-card', 'count-online', 'review']
/**
 * F-CTRL-N67: on a day with marketplace (Foodpanda) sales the wizard grows a
 * fourth counting step. The manager is always holding the partner-app total at
 * close; before this step the only box on screen was the online one, so that is
 * where the number went — and the Z report then announced "Online over by
 * PKR 37,240" on a day that was fine (Kim Mun, 2026-09-04/05). Days without
 * marketplace sales never see the step.
 */
const MARKETPLACE_STEP_ORDER: Step[] = [
  'count-cash',
  'count-card',
  'count-online',
  'count-marketplace',
  'review',
]

/**
 * End-of-day wizard, redesigned for cashiers. Internally still uses the
 * two-phase stage/lock backend pattern so we can show a clear retry path
 * if the lock call fails after staging succeeds — but the user never sees
 * the word "staged".
 *
 *   Step 1: count the cash in the drawer
 *   Step 2: card machine total
 *   Step 3: online & wallet total (Foodpanda excluded)
 *   Step 3b: Foodpanda partner-app total — only on days with marketplace sales (F-CTRL-N67)
 *   Step 4: review the differences and finish
 *
 * On Finish: stage-close → lock-close, fired back-to-back. If lock fails,
 * the dialog stays open on the review step with a clear retry button.
 *
 * F-CTRL-N33 reshaped steps 2–3. They used to be a single optional screen —
 * two bare inputs, a Skip button, and no keypad, so on a touchscreen till the
 * card and online totals could not be entered at all. Skipping stored NULL,
 * computed no variance, and left the review screen silent about both tenders,
 * so a card shortfall was invisible to everyone. Now each tender gets its own
 * step with its own keypad and cannot be passed without a number. Splitting
 * them (rather than putting one keypad above two fields) is deliberate: a
 * shared keypad needs an active-field concept, and typing the card total into
 * the online box would reconcile the day to a lie.
 */
export function CloseDayWizard({
  open,
  onOpenChange,
  day,
  cashSales,
  cardSales,
  onlineSales,
  platformSettledOnline = 0,
  marketplaceLabel = 'Foodpanda',
  unresolvedOpenOrders,
  movements,
  isLoadingSummary,
  tabSettlements,
}: CloseDayWizardProps) {
  const tabCash = tabSettlements?.cash ?? 0
  const tabCard = tabSettlements?.card ?? 0
  const tabOnline = tabSettlements?.online ?? 0
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()
  const hasMarketplace = platformSettledOnline > 0
  const STEP_ORDER = hasMarketplace ? MARKETPLACE_STEP_ORDER : BASE_STEP_ORDER

  const adj = useMemo(() => netCashAdjustments(movements), [movements])
  // F-CTRL-N75: stock cash-outs whose items were never added. Not a blocker —
  // the cash is right; only the stock is missing, and it can be added later.
  const stockNotAdded = countNeedingItems(movements)
  const paidIn = useMemo(
    () =>
      (movements ?? [])
        .filter((m) => m.movement_type === 'paid_in')
        .reduce((sum, m) => sum + m.amount, 0),
    [movements],
  )
  const paidOut = useMemo(
    () =>
      (movements ?? [])
        .filter((m) => m.movement_type === 'paid_out')
        .reduce((sum, m) => sum + m.amount, 0),
    [movements],
  )
  const expectedCash = (day.opening_cash ?? 0) + cashSales + adj

  const [step, setStep] = useState<Step>('count-cash')
  const [countedCash, setCountedCash] = useState<string>('')
  const [countedCard, setCountedCard] = useState<string>('')
  const [countedOnline, setCountedOnline] = useState<string>('')
  const [countedMarketplace, setCountedMarketplace] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [showNotes, setShowNotes] = useState(false)
  /** Surface a friendly retry banner if lock failed after stage succeeded. */
  const [lockFailed, setLockFailed] = useState(false)

  // Reset wizard state only when the dialog opens — NOT every time the parent
  // refetches `day`. The previous dep list included day.status / day.counted_*
  // which caused a refetch mid-edit to wipe the user's typed values and (worse)
  // clobber `lockFailed=true` back to false the instant the staged-row refetch
  // landed, so the "Almost done — tap Finish to lock" banner would flicker.
  useEffect(() => {
    if (open) {
      const isStaged = day.status === 'staged'
      const cash = isStaged && day.counted_cash != null ? String(day.counted_cash) : ''
      const card = isStaged && day.counted_card != null ? String(day.counted_card) : ''
      const online = isStaged && day.counted_online != null ? String(day.counted_online) : ''
      const marketplace =
        isStaged && day.counted_marketplace != null ? String(day.counted_marketplace) : ''
      setCountedCash(cash)
      setCountedCard(card)
      setCountedOnline(online)
      setCountedMarketplace(marketplace)
      // Jump to review only when the staged row actually carries all three
      // counts. A day staged by an older build has NULL card/online: landing
      // on review would paint steps 2–3 green (StepIndicator colours every
      // step below the current one) for screens the operator never saw, and
      // the backend would then refuse the lock with tender_count_required.
      // Walk the wizard instead, pre-filled with whatever is known.
      const complete =
        cash !== '' && card !== '' && online !== '' && (!hasMarketplace || marketplace !== '')
      setStep(isStaged && complete ? 'review' : 'count-cash')
      setNotes(isStaged && day.closing_notes ? day.closing_notes : '')
      setShowNotes(false)
      setLockFailed(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const parsedCash = Number(countedCash) || 0
  const parsedCard = Number(countedCard) || 0
  const parsedOnline = Number(countedOnline) || 0
  const parsedMarketplace = Number(countedMarketplace) || 0
  const cashVariance = parsedCash - expectedCash
  // A blank field is NOT "Rs 0 matches" — it is "nobody counted this". Each
  // step's Next stays disabled until a real number is typed, so by the review
  // step all three of these are true (F-CTRL-N33).
  const cardEntered = countedCard.trim() !== '' && Number.isFinite(Number(countedCard))
  const onlineEntered = countedOnline.trim() !== '' && Number.isFinite(Number(countedOnline))
  const marketplaceEntered =
    countedMarketplace.trim() !== '' && Number.isFinite(Number(countedMarketplace))
  const isCountValid = countedCash.trim() !== '' && Number.isFinite(Number(countedCash))

  const thresholdQuery = useQuery({
    queryKey: ['settings', 'day_close_variance_threshold'],
    queryFn: () => apiClient.getSetting('day_close_variance_threshold'),
    staleTime: 5 * 60_000,
  })
  const varianceThreshold = useMemo(() => {
    const raw = thresholdQuery.data?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_VARIANCE_THRESHOLD
  }, [thresholdQuery.data])

  // Cross-day tender bridge: card/online swiped TODAY for orders from earlier
  // days. On today's machine/bank batch but booked to those days, so it explains
  // a card/online figure that reads higher than "our records" (the Rasoi case).
  const tenderReconQuery = useQuery({
    queryKey: ['day-ops', 'tender-recon', day.id],
    queryFn: () => apiClient.getTenderReconciliation(day.id),
    enabled: open,
    staleTime: 60_000,
  })
  const tenderRecon = tenderReconQuery.data?.success ? tenderReconQuery.data.data : null
  const absVariance = Math.abs(cashVariance)
  const isBigOff = absVariance > varianceThreshold

  // Card/online reconcile against a POINT, not a band: the machine/bank batch is
  // expected to total sales + cross-day exactly. Cross-day payments (swiped today
  // for earlier orders, whose sale books on those days — see the note above) were
  // physically swiped today, so they land on today's batch for certain. Anything
  // short of the expected point is money missing from the batch; anything over is
  // money on the batch we can't account for. Both, like cash, need a reason above
  // the threshold. (A band [sales, sales+crossDay] used to let a large crossDay
  // swallow a genuine shortfall — e.g. Rs 50,000 entered against Rs 174,098
  // expected read as "reconciles".)
  const cardCrossDay = tenderRecon?.card_cross_day ?? 0
  const onlineCrossDay = tenderRecon?.online_cross_day ?? 0
  const cardExpected = cardSales + cardCrossDay
  const onlineExpected = onlineSales + onlineCrossDay
  const cardOver = cardEntered ? Math.max(0, parsedCard - cardExpected) : 0
  const cardShort = cardEntered ? Math.max(0, cardExpected - parsedCard) : 0
  const cardUnexplained = cardOver + cardShort
  const onlineOver = onlineEntered ? Math.max(0, parsedOnline - onlineExpected) : 0
  const onlineShort = onlineEntered ? Math.max(0, onlineExpected - parsedOnline) : 0
  const onlineUnexplained = onlineOver + onlineShort
  // F-CTRL-N67: partner-app total vs what the POS rang under the marketplace
  // type. Not a drawer — a gap means an order was rung wrong (or not at all),
  // and above the threshold it needs the same note as any other tender.
  const marketplaceExpected = platformSettledOnline
  const marketplaceDelta = marketplaceEntered ? parsedMarketplace - marketplaceExpected : 0
  const marketplaceOver = Math.max(0, marketplaceDelta)
  const marketplaceShort = Math.max(0, -marketplaceDelta)
  const marketplaceUnexplained = marketplaceOver + marketplaceShort
  const varianceNeedsNote =
    isBigOff ||
    cardUnexplained > varianceThreshold ||
    onlineUnexplained > varianceThreshold ||
    (hasMarketplace && marketplaceUnexplained > varianceThreshold)
  // F-CTRL-N25: real open orders (served-but-unpaid / partially-paid tabs) still
  // holding tables & revenue must be settled/cancelled or acknowledged with a
  // note before the day locks — the backend LockClose enforces the same gate.
  const openOrdersNeedNote = unresolvedOpenOrders > 0
  const needsNote = varianceNeedsNote || openOrdersNeedNote
  const noteRequired = needsNote && notes.trim().length === 0

  // Per-tender breakdown for the review step (step 3): so the manager sees cash,
  // card and online short/over together and can explain them all in one note,
  // instead of the headline banner showing only the cash figure.
  const tenderBreakdown: TenderRow[] = [
    {
      label: 'Cash',
      entered: isCountValid,
      over: cashVariance > 0 ? cashVariance : 0,
      short: cashVariance < 0 ? -cashVariance : 0,
      needsNote: absVariance > varianceThreshold,
    },
    {
      label: 'Card',
      entered: cardEntered,
      over: cardOver,
      short: cardShort,
      needsNote: cardUnexplained > varianceThreshold,
    },
    {
      label: 'Online',
      entered: onlineEntered,
      over: onlineOver,
      short: onlineShort,
      needsNote: onlineUnexplained > varianceThreshold,
    },
    ...(hasMarketplace
      ? [
          {
            label: `${marketplaceLabel} app`,
            entered: marketplaceEntered,
            over: marketplaceOver,
            short: marketplaceShort,
            needsNote: marketplaceUnexplained > varianceThreshold,
            // Nothing sits in a drawer here: the app either agrees with the
            // POS or shows more / less than was rung.
            phrasing: { over: 'more in app', short: 'less in app' },
          } satisfies TenderRow,
        ]
      : []),
  ]

  // Headline state across EVERY tender, not just cash.
  //
  // The banner used to report `cashVariance` alone, so a day could announce
  // "Rs 15,997 short" while card was Rs 17,589 short and online Rs 2,142 over —
  // the larger problem invisible in the one line most people read. Now that all
  // three are always counted (F-CTRL-N33), the headline has to speak for all
  // three.
  //
  // Deliberately NOT a summed figure: cash, card and online are separate pots
  // (drawer, machine batch, bank), and netting an over against a short would
  // hide that two different things went wrong. When more than one is off we
  // state how many and let the breakdown below carry the numbers, rather than
  // inventing a composite total whose meaning nobody could explain.
  const offTenders = tenderBreakdown.filter((t) => t.over > 0.005 || t.short > 0.005)
  const anyTenderNeedsNote = tenderBreakdown.some((t) => t.needsNote)

  // The arithmetic behind each expected figure (F-FE-N139). Shown one tap
  // away on the count step and again under that tender's row on the review.
  const crossDayWhy =
    'The older bills were opened on earlier days, so their sales are booked there. The money still lands tonight, so the expected figure includes it.'
  const cardBreakdown: ExpectedBreakdownSpec = {
    rows: [
      { label: "Card taken on today's orders", amount: cardSales - tabCard },
      ...(tabCard > 0 ? [{ label: 'Tab payments by card', amount: tabCard, sign: '+' as const }] : []),
      ...(cardCrossDay > 0
        ? [{ label: 'Card taken today for older open bills', amount: cardCrossDay, sign: '+' as const }]
        : []),
    ],
    total: { label: "On tonight's machine batch", amount: cardExpected },
    why: cardCrossDay > 0 ? crossDayWhy : undefined,
  }
  const onlineBreakdown: ExpectedBreakdownSpec = {
    rows: [
      { label: "Online on today's orders", amount: onlineSales - tabOnline },
      ...(tabOnline > 0 ? [{ label: 'Tab payments online', amount: tabOnline, sign: '+' as const }] : []),
      ...(onlineCrossDay > 0
        ? [{ label: 'Online taken today for older open bills', amount: onlineCrossDay, sign: '+' as const }]
        : []),
      ...(hasMarketplace
        ? [
            {
              label: `Not counted: ${marketplaceLabel} pays you later`,
              amount: platformSettledOnline,
              tone: 'muted' as const,
            },
          ]
        : []),
    ],
    total: { label: 'Should be in your accounts', amount: onlineExpected },
    why:
      hasMarketplace && onlineExpected < 0.005
        ? `Every online order today was ${marketplaceLabel}, and that money is not in your bank tonight. Type 0 here; the app total has its own step next.`
        : [
            hasMarketplace
              ? `${marketplaceLabel} money is not in your bank tonight. It is checked on its own step, so leave it out here.`
              : '',
            onlineCrossDay > 0 ? crossDayWhy : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined,
  }
  const marketplaceBreakdown: ExpectedBreakdownSpec = {
    rows: [{ label: `${marketplaceLabel} orders rung on the POS`, amount: marketplaceExpected }],
    total: { label: 'The app should show', amount: marketplaceExpected },
    why: `This money is not in your drawer, card machine or bank tonight. ${marketplaceLabel} pays it to you later, after its commission. This step only checks that the app and the POS agree.`,
  }
  const cashBreakdown: ExpectedBreakdownSpec = {
    rows: [
      { label: 'Opening float', amount: day.opening_cash ?? 0 },
      { label: 'Cash sales', amount: cashSales - tabCash, sign: '+' },
      ...(tabCash > 0 ? [{ label: 'Tab payments in cash', amount: tabCash, sign: '+' as const }] : []),
      ...(paidIn > 0 ? [{ label: 'Money put in', amount: paidIn, sign: '+' as const }] : []),
      ...(paidOut > 0 ? [{ label: 'Money taken out', amount: paidOut, sign: '−' as const }] : []),
    ],
    total: { label: 'Should be in the drawer', amount: expectedCash },
  }
  const reviewRows: ReviewRow[] = [
    {
      key: 'cash',
      label: 'Cash',
      expected: expectedCash,
      counted: parsedCash,
      delta: cashVariance,
      breakdown: cashBreakdown,
    },
    {
      key: 'card',
      label: 'Card machine',
      expected: cardExpected,
      counted: parsedCard,
      delta: parsedCard - cardExpected,
      breakdown: cardBreakdown,
    },
    {
      key: 'online',
      label: 'Online & wallet',
      expected: onlineExpected,
      counted: parsedOnline,
      delta: parsedOnline - onlineExpected,
      breakdown: onlineBreakdown,
    },
    ...(hasMarketplace
      ? [
          {
            key: 'marketplace',
            label: `${marketplaceLabel} app`,
            sublabel: 'paid to you later, not in tonight\'s money',
            expected: marketplaceExpected,
            counted: parsedMarketplace,
            delta: marketplaceDelta,
            phrasing: { over: 'more in app', short: 'less in app' },
            breakdown: marketplaceBreakdown,
          } satisfies ReviewRow,
        ]
      : []),
  ]
  // "Cash and Card are short" / "Cash, Card and Online don't match" — names
  // the tenders the required note is about.
  const offSummary = (() => {
    const off = tenderBreakdown.filter((t) => t.needsNote)
    if (off.length === 0) return ''
    const names = off.map((t) => t.label)
    const list =
      names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    const allShort = off.every((t) => t.short > 0)
    const allOver = off.every((t) => t.over > 0)
    const verb = off.length === 1 ? 'is' : 'are'
    return allShort ? `${list} ${verb} short` : allOver ? `${list} ${verb} over` : `${list} don't match`
  })()

  const stageMut = useMutation({
    mutationFn: () =>
      apiClient.stageCloseDay(day.id, {
        counted_cash: parsedCash,
        // All three are always sent (F-CTRL-N33). The steps above cannot be
        // advanced past without a number, so these are counted figures — an
        // explicit 0 means "this tender took nothing", never "not counted".
        counted_card: parsedCard,
        counted_online: parsedOnline,
        // F-CTRL-N67: only on days that had marketplace sales — the step is
        // not shown otherwise, and an omitted field is stored as "not counted".
        counted_marketplace: hasMarketplace ? parsedMarketplace : undefined,
        notes: notes.trim() || undefined,
      }),
  })

  const lockMut = useMutation({
    mutationFn: () => apiClient.lockCloseDay(day.id),
  })

  const finishing = stageMut.isPending || lockMut.isPending

  const finish = async () => {
    setLockFailed(false)
    // Always re-stage before lock — even if the day is already 'staged'
    // from a previous attempt. Reasons:
    //   1. User may have come back from review → step 1 to fix a miscount.
    //      That edited value lives only in local state until stage runs.
    //   2. User may have just typed a closing note. closing_notes is
    //      persisted by StageClose, not LockClose — without re-staging,
    //      the note never reaches the DB and the variance gate would
    //      still reject the lock.
    //   3. Fresh sales / cash movements may have landed since the prior
    //      stage. Re-staging recomputes expected_cash + cash_variance so
    //      the lock check operates on current truth, not stale numbers
    //      from the first attempt.
    // Backend StageClose accepts StateOpen | StateStaged | StateReopened
    // (dayops/service.go:931) — idempotent overwrite is the documented
    // path. The extra audit row each retry produces is accurate history.
    //
    // Two try/catches so we can tell stage-failure from lock-failure
    // without reading a possibly-stale `stageMut.isSuccess` after an
    // awaited mutation (the hook snapshot in this closure is from the
    // render that started this handler).
    try {
      await stageMut.mutateAsync()
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      if (handleTenderCountRequired(err)) return
      toastHelpers.apiError('Save counted amounts', err as Error)
      return
    }
    try {
      await lockMut.mutateAsync()
      toastHelpers.success('Today is finished and locked')
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['dailyClosings'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      onOpenChange(false)
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      if (handleTenderCountRequired(err)) return
      if (handleUnreconciledOfflineItems(err)) return
      setLockFailed(true)
      toastHelpers.apiError('Finish the day', err as Error)
    }
  }

  /**
   * F-CTRL-N35: the backend refused because food fired from an offline terminal
   * was never billed. There is deliberately no note override for this one, so
   * the only way forward is to resolve the items — say exactly that, and point
   * at where the list lives, rather than leaving the manager to guess from a
   * generic failure at 2am.
   */
  const handleUnreconciledOfflineItems = (err: unknown): boolean => {
    if (!(err instanceof ApiClientError) || err.code !== 'unreconciled_offline_items') return false
    setLockFailed(true)
    qc.invalidateQueries({ queryKey: ['offline', 'unreconciled'] })
    toastHelpers.error(
      'Unbilled kitchen items',
      'Some items were sent to the kitchen from an offline terminal and never billed. ' +
        'Resolve them in the “Fired but unbilled” panel at the top of Day Close, then finish.',
    )
    return true
  }

  /**
   * F-CTRL-N33: the backend refused because a tender was never counted. Send
   * the operator to that step instead of leaving them on a Finish button that
   * keeps failing. In normal use this is unreachable — the wizard always sends
   * all three — so it exists for a stale tab running an older bundle, or a day
   * staged by a build that predates this rule.
   */
  const handleTenderCountRequired = (err: unknown): boolean => {
    if (!(err instanceof ApiClientError) || err.code !== 'tender_count_required') return false
    const missing = err.missingTenders ?? []
    setLockFailed(false)
    setStep(missing.includes('card') || missing.length === 0 ? 'count-card' : 'count-online')
    toastHelpers.error(
      'Count this first',
      'Enter the total for every payment type before finishing — type 0 if it took nothing.',
    )
    return true
  }

  // Linear navigation. Stepping back is allowed even after staging: backend
  // StageClose is idempotent on a staged day, so the manager can step back,
  // fix a miscount, and finish() re-stages with the new value. Clearing
  // lockFailed on the way back means the amber "tap Finish to lock" retry
  // banner doesn't linger across an explicit re-count.
  const goToStep = (next: Step) => {
    setLockFailed(false)
    setStep(next)
  }
  const stepIndex = STEP_ORDER.indexOf(step)
  const goNext = () => goToStep(STEP_ORDER[Math.min(stepIndex + 1, STEP_ORDER.length - 1)])
  const goBack = () => goToStep(STEP_ORDER[Math.max(stepIndex - 1, 0)])

  // Whether the current step's answer is present. Every counting step is
  // mandatory — no Skip, and Next stays dead until a number is typed.
  const canAdvance =
    step === 'count-cash'
      ? isCountValid
      : step === 'count-card'
        ? cardEntered
        : step === 'count-online'
          ? onlineEntered
          : step === 'count-marketplace'
            ? marketplaceEntered
            : true

  return (
    <Dialog open={open} onOpenChange={(o) => !finishing && onOpenChange(o)}>
      <DialogContent className="flex max-h-[min(92dvh,900px)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1 border-b px-6 py-4 pr-12 text-left">
          <DialogTitle className="text-2xl">
            {step === 'count-cash' && 'Count the drawer'}
            {step === 'count-card' && 'Card machine'}
            {step === 'count-online' && 'Online & wallet'}
            {step === 'count-marketplace' && `${marketplaceLabel} app`}
            {step === 'review' && "Today's count"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {step === 'count-cash' &&
              'Count every note and coin in the till and type the total.'}
            {step === 'count-card' &&
              "Type the day total from the card machine's settlement report. If it took nothing, type 0."}
            {step === 'count-online' &&
              (hasMarketplace
                ? `Money that reached your bank, Easypaisa or JazzCash today. ${marketplaceLabel} has its own step next.`
                : 'Money that reached your bank, Easypaisa or JazzCash today. If none, type 0.')}
            {step === 'count-marketplace' &&
              `Open the ${marketplaceLabel} partner app, Today tab, and type the total at the top of Recent Orders.`}
            {step === 'review' &&
              'Check each line, write a note if something is off, then finish to lock the day.'}
          </DialogDescription>
          <StepIndicator step={step} order={STEP_ORDER} />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 'count-cash' && (
            <TenderCountStep
              id="counted-cash"
              label="Money you counted in the drawer"
              value={countedCash}
              onChange={setCountedCash}
              formatCurrency={formatCurrency}
            />
          )}

          {step === 'count-card' && (
            <TenderCountStep
              id="counted-card"
              label="Total on the card machine"
              value={countedCard}
              onChange={setCountedCard}
              formatCurrency={formatCurrency}
              expected={cardExpected}
              delta={cardEntered ? parsedCard - cardExpected : undefined}
              threshold={varianceThreshold}
              breakdown={cardBreakdown}
              idleText="Type the machine total to compare"
            />
          )}

          {step === 'count-online' && (
            <TenderCountStep
              id="counted-online"
              label="Total received online and by wallet"
              value={countedOnline}
              onChange={setCountedOnline}
              formatCurrency={formatCurrency}
              expected={onlineExpected}
              delta={onlineEntered ? parsedOnline - onlineExpected : undefined}
              threshold={varianceThreshold}
              breakdown={onlineBreakdown}
              idleText="Type the bank and wallet total to compare"
            />
          )}

          {step === 'count-marketplace' && (
            <TenderCountStep
              id="counted-marketplace"
              label={`Total in the ${marketplaceLabel} app today`}
              value={countedMarketplace}
              onChange={setCountedMarketplace}
              formatCurrency={formatCurrency}
              expected={marketplaceExpected}
              delta={marketplaceEntered ? marketplaceDelta : undefined}
              threshold={varianceThreshold}
              phrasing={{ over: 'more in app', short: 'less in app' }}
              breakdown={marketplaceBreakdown}
              idleText="Type the app total to compare"
            />
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <ReviewStatus
                offTenders={offTenders}
                totalTenders={tenderBreakdown.length}
                needsNote={anyTenderNeedsNote}
                loading={isLoadingSummary}
                formatCurrency={formatCurrency}
              />

              {/* Always rendered (F-CTRL-N33). Every tender is counted by the
                  time we reach this step, so the review reads as a complete
                  statement — the old gate hid card and online entirely whenever
                  the step had been skipped, which is exactly how a card
                  shortfall stayed invisible. */}
              <ReviewTable
                rows={reviewRows}
                threshold={varianceThreshold}
                formatCurrency={formatCurrency}
              />

              {stockNotAdded > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>{BT.closeNote(stockNotAdded)}</span>
                </div>
              )}

              {openOrdersNeedNote && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="text-amber-900 dark:text-amber-200">
                    <span className="font-semibold">
                      {unresolvedOpenOrders} open order{unresolvedOpenOrders === 1 ? '' : 's'} still unresolved
                    </span>{' '}
                    — settle or cancel them (Open Orders on the counter), or add a note below explaining
                    why they're left open, before the day can lock.
                  </div>
                </div>
              )}

              {showNotes || needsNote ? (
                <div className="space-y-2">
                  <Label htmlFor="closing-notes" className="text-base">
                    Note for the owner
                    {needsNote ? (
                      <span className="ml-1 text-sm font-normal text-rose-700 dark:text-rose-300">
                        {varianceNeedsNote && openOrdersNeedNote
                          ? `(required, ${offSummary} and there are open orders)`
                          : openOrdersNeedNote
                            ? '(required, acknowledge the open orders)'
                            : `(required, ${offSummary})`}
                      </span>
                    ) : (
                      <span className="ml-1 text-sm font-normal text-muted-foreground">
                        (optional)
                      </span>
                    )}
                  </Label>
                  <Textarea
                    id="closing-notes"
                    rows={2}
                    placeholder={
                      needsNote
                        ? 'e.g. drawer counted before the last table paid; card machine not settled yet; Table 5 still dining'
                        : 'Anything the owner should know about today'
                    }
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={cn(
                      noteRequired && 'border-rose-400 focus-visible:ring-rose-400',
                    )}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNotes(true)}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" /> Add a note for the owner (optional)
                </button>
              )}

              {lockFailed && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
                  <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-amber-900 dark:text-amber-200">
                      Almost done — tap Finish to lock
                    </div>
                    <div className="text-sm text-amber-800 dark:text-amber-300">
                      Your counted amounts were saved. The last step didn't finish, so tap
                      Finish and lock today again.
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Once you finish, today's numbers are saved and only the owner can change them.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4">
          {/* No Skip on any counting step (F-CTRL-N33) — every tender is
              counted, and "it took nothing" is entered as a typed 0. */}
          {step !== 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={step === 'count-cash' ? () => onOpenChange(false) : goBack}
              >
                {step === 'count-cash' ? 'Cancel' : 'Back'}
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 sm:flex-none"
                onClick={goNext}
                disabled={!canAdvance}
              >
                Next <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={goBack}
                disabled={finishing}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={finish}
                disabled={finishing || noteRequired || isLoadingSummary}
              >
                {finishing ? (
                  'Saving…'
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Finish and lock today
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
